import { AddInError, getEwsResponseSizeCap } from '@utils-global';
import { getEWSRequestBodyTemplate, getSizeBodyTemplate, parseResponse } from './helpers';

// EWS returns MimeContent base64-encoded inside the SOAP response, inflating it
// by ~4/3. The extra factor leaves margin for the surrounding envelope/properties.
const BASE64_OVERHEAD = 1.37;

export type EwsMimeFailureCause =
  | 'tooLarge' // projected MIME response exceeds the makeEwsRequestAsync cap
  | 'itemNotFound' // item moved/deleted or not yet synced in cached mode
  | 'accessDenied' // no rights on the item/mailbox
  | 'serverBusy' // EWS throttling — transient
  | 'mimeGeneration' // server couldn't produce valid MIME for the item
  | 'sharedFolderUnsupported' // makeEwsRequestAsync isn't supported for shared folders
  | 'offline' // client has no network
  | 'probeFailed' // even a tiny request failed - environmental, not item-specific
  | 'ewsError' // a named EWS error we don't special-case
  | 'unknown'; // item is fine and not oversized - likely transient/internal

export type EwsMimeDiagnosis = {
  cause: EwsMimeFailureCause;
  responseCode?: string;
  messageText?: string;
  itemSizeBytes?: number;
  estimatedMimeBytes?: number;
  capBytes?: number;
  exceeds?: boolean;
};

export class EwsMimeError extends AddInError {
  cause: EwsMimeFailureCause;

  constructor(message: string, cause: EwsMimeFailureCause, context?: Record<string, unknown>) {
    super(message, context);
    this.name = 'EwsMimeError';
    this.cause = cause;
    Object.setPrototypeOf(this, EwsMimeError.prototype);
  }
}

// Server failed to produce valid MIME: conversion failed, invalid, or bad base64.
const MIME_GENERATION_CODES = [
  'ErrorMimeContentConversionFailed',
  'ErrorMimeContentInvalid',
  'ErrorMimeContentInvalidBase64String',
];

/**
 * Maps an EWS ResponseCode to a failure cause. Shared by both failure modes.
 * https://learn.microsoft.com/exchange/client-developer/web-service-reference/responsecode
 */
export const mapEwsResponseCode = (responseCode: string): EwsMimeFailureCause => {
  // Message exceeds the server's configured size limit.
  if (responseCode === 'ErrorMessageSizeExceeded') return 'tooLarge';
  // Item moved/deleted, or not yet synced to the server in cached mode.
  if (responseCode === 'ErrorItemNotFound') return 'itemNotFound';
  // Caller lacks rights on the item or mailbox.
  if (responseCode === 'ErrorAccessDenied') return 'accessDenied';
  // EWS throttling - transient, safe to retry.
  if (responseCode === 'ErrorServerBusy') return 'serverBusy';
  // Server couldn't generate/return valid MIME for the item.
  if (MIME_GENERATION_CODES.includes(responseCode)) return 'mimeGeneration';
  // Any other named EWS error (kept verbatim in telemetry).
  return 'ewsError';
};

/**
 * Diagnoses why an EWS getMailMIME call failed with a generic 9020 (which carries
 * no response body). Re-issues a tiny GetItem (IdOnly + item:Size) that fits under
 * the makeEwsRequestAsync cap: its own ResponseCode names item-level errors, and a
 * clean success lets us compare the projected MIME size against the cap.
 *
 * Best-effort only, every branch resolves a diagnosis and nothing throws.
 */
export const diagnoseEwsMimeFailure = (mailId: string) =>
  new Promise<EwsMimeDiagnosis>((resolve) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      resolve({ cause: 'offline' });
      return;
    }

    const requestBody = getEWSRequestBodyTemplate(getSizeBodyTemplate(mailId));

    Office.context.mailbox.makeEwsRequestAsync(requestBody, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        // A tiny request failed too, not item-specific (network / EWS config / transient).
        resolve({ cause: 'probeFailed' });
        return;
      }

      const [size, responseCode, messageText] = parseResponse(result.value, [
        'Size',
        'ResponseCode',
        'MessageText',
      ]);

      // The probe completed but EWS reported an item-level error that names the cause.
      if (responseCode && responseCode !== 'NoError') {
        resolve({
          cause: mapEwsResponseCode(responseCode),
          responseCode,
          messageText: messageText ?? undefined,
        });
        return;
      }

      const itemSizeBytes = Number(size);
      if (!itemSizeBytes || Number.isNaN(itemSizeBytes)) {
        resolve({ cause: 'unknown' });
        return;
      }

      // Item exists and is reachable; the only difference from the failed MIME call
      // is the payload size, so size is the explanation if it's over the cap.
      const capBytes = getEwsResponseSizeCap();
      const estimatedMimeBytes = Math.round(itemSizeBytes * BASE64_OVERHEAD);
      const exceeds = estimatedMimeBytes >= capBytes;

      resolve({
        cause: exceeds ? 'tooLarge' : 'unknown',
        itemSizeBytes,
        estimatedMimeBytes,
        capBytes,
        exceeds,
      });
    });
  });

export const classifyEwsMimeFailure = async (
  officeError: Office.Error | undefined,
  mailId: string,
  isSharedMailbox: boolean,
): Promise<EwsMimeDiagnosis> => {
  // 9020 (GenericResponseError) is a catch-all with no response body, probe for a cause.
  // Any other Office-level failure is a named EWS error we don't special-case.
  if (officeError?.code !== 9020) return { cause: 'ewsError' };

  const diagnosis = await diagnoseEwsMimeFailure(mailId);

  // probeFailed = the probe's own makeEwsRequestAsync couldn't run. On a shared mailbox
  // that's the shared-folder limitation; a probe that ran (any other cause) rules it out.
  if (isSharedMailbox && diagnosis.cause === 'probeFailed') {
    return { cause: 'sharedFolderUnsupported' };
  }

  return diagnosis;
};
